import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../../../lib/api";

const ACT_COLORS = ["bg-violet", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-pink-500", "bg-cyan-500", "bg-orange-500"];

export default function GanttTab({ plan }) {
  const [run, setRun] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState("gantt");
  const [zoom, setZoom] = useState(14); // px por día
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get(`/planning/plans/${plan.id}/schedule`).then((r) => setRun(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, [plan.id, plan.version]);

  const actColors = useMemo(() => {
    const map = {};
    (plan.activities || []).forEach((a, i) => { map[a.code] = ACT_COLORS[i % ACT_COLORS.length]; });
    return map;
  }, [plan.activities]);

  const grouped = useMemo(() => {
    if (!run) return [];
    const by = {};
    for (const it of run.schedule) {
      if (filter && !it.wu_label.toLowerCase().includes(filter.toLowerCase())) continue;
      (by[it.wu_id] = by[it.wu_id] || { label: it.wu_label, items: [] }).items.push(it);
    }
    return Object.values(by).sort((a, b) => Math.min(...a.items.map((i) => i.start)) - Math.min(...b.items.map((i) => i.start)));
  }, [run, filter]);

  if (!loaded) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy/30" /></div>;
  if (!run) return <div className="rounded-xl border border-dashed border-navy/15 bg-white p-14 text-center text-sm text-navy/40" data-testid="gantt-empty">Genera el plan para ver el Gantt y la vista de recursos.</div>;

  const stats = run.stats || {};
  const maxDay = Math.max(stats.dias_cronograma || 1, 1);
  const days = Array.from({ length: maxDay }, (_, i) => i + 1);
  const bufferStart = stats.dias_habiles_disponibles - (stats.buffer_dias || 0);

  return (
    <div className="space-y-4" data-testid="gantt-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-full border border-navy/15 overflow-hidden text-sm">
          {[["gantt", "Gantt"], ["recursos", "Recursos"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
              className={`px-4 py-2 transition-colors ${view === k ? "bg-navy text-cream" : "text-navy/50 hover:text-navy"}`}>{l}</button>
          ))}
        </div>
        {view === "gantt" && (
          <>
            <input className="bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet w-44"
              placeholder="Filtrar unidad…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="gantt-filter" />
            <div className="flex items-center gap-1 text-xs text-navy/40">
              Zoom <input type="range" min={6} max={40} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
            </div>
          </>
        )}
        <div className="ml-auto flex flex-wrap gap-3 text-[11px] text-navy/50">
          {(plan.activities || []).map((a) => (
            <span key={a.code} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm ${actColors[a.code]}`} /> {a.name}</span>
          ))}
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm border-2 border-red-500" /> Ruta crítica</span>
        </div>
      </div>

      <div className="rounded-lg border border-navy/10 bg-white px-4 py-2.5 text-xs text-navy/50 flex flex-wrap gap-x-5 gap-y-1" data-testid="gantt-stats">
        <span><b className="text-navy/70">{stats.dias_equivalentes}</b> días equivalentes</span>
        <span><b className="text-navy/70">{stats.dias_habiles_disponibles}</b> días hábiles</span>
        {stats.buffer_dias > 0 && <span><b className="text-navy/70">{stats.buffer_dias}</b> buffer</span>}
        <span>Fin proyectado <b className="text-navy/70">{stats.fecha_fin_proyectada}</b></span>
        <span className={stats.en_tiempo ? "text-emerald-600" : "text-red-600"}>{stats.en_tiempo ? "✓ En tiempo" : `⚠ Excede por ${Math.abs(stats.variacion_dias)} día(s)`}</span>
        <span>Cuello de botella: <b className="text-navy/70">{stats.cuello_botella}</b></span>
      </div>

      {view === "gantt" ? (
        <div className="rounded-xl border border-navy/10 bg-white overflow-auto max-h-[560px]" data-testid="gantt-chart">
          <div style={{ minWidth: 190 + maxDay * zoom }}>
            <div className="flex sticky top-0 bg-white z-10 border-b border-navy/10">
              <div className="w-[190px] shrink-0 px-3 py-2 text-xs text-navy/40 font-medium sticky left-0 bg-white">{plan.work_unit_name}</div>
              {days.map((d) => (
                <div key={d} className={`shrink-0 text-center text-[9px] py-2 ${d > bufferStart ? "bg-orange-50 text-orange-600" : "text-navy/30"}`} style={{ width: zoom }}>
                  {zoom >= 12 || d % 5 === 0 ? d : ""}
                </div>
              ))}
            </div>
            {grouped.map((g) => (
              <div key={g.label} className="flex border-b border-navy/5 hover:bg-navy/[0.02] group">
                <div className="w-[190px] shrink-0 px-3 py-1 text-xs truncate sticky left-0 bg-white group-hover:bg-[#fafafa]" title={g.label}>
                  {g.label}
                </div>
                <div className="relative flex-1" style={{ height: 22 }}>
                  {g.items.map((it, i) => (
                    <div key={i} title={`${it.activity_name} · ${it.resource} · días ${it.start_day}–${it.end_day} (${it.start_date} → ${it.end_date})${it.critical ? " · CRÍTICO" : ""} · holgura ${it.slack}d${it.status === "completada" ? " · COMPLETADA" : ""}`}
                      className={`absolute top-[4px] h-[14px] rounded-sm ${actColors[it.activity] || "bg-navy/40"} ${it.critical ? "ring-2 ring-red-500" : ""} ${it.status === "completada" ? "opacity-40" : "opacity-90"} hover:opacity-100 cursor-default`}
                      style={{ left: it.start * zoom, width: Math.max((it.finish - it.start) * zoom, 3) }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="resource-view">
          {(run.utilization || []).map((u) => {
            const seq = run.schedule.filter((it) => it.resource === u.recurso).sort((a, b) => a.start - b.start);
            return (
              <div key={u.recurso} className="rounded-xl border border-navy/10 bg-white p-4" data-testid={`resource-${u.recurso}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-medium text-sm">{u.recurso}</span>
                  <div className="w-40 h-2 rounded-full bg-navy/10 overflow-hidden">
                    <div className={`h-full ${u.utilizacion > 0.9 ? "bg-red-500" : u.utilizacion > 0.7 ? "bg-amber-500" : "bg-emerald-500"}`}
                      style={{ width: `${Math.min(u.utilizacion * 100, 100)}%` }} />
                  </div>
                  <span className="text-xs text-navy/50">{Math.round(u.utilizacion * 100)}% · {u.dias_ocupado} días ocupado · {seq.length} asignaciones</span>
                </div>
                <div className="text-[11px] text-navy/40 mt-2 truncate" title={seq.map((s) => s.wu_label).join(" → ")}>
                  {seq.slice(0, 14).map((s) => s.wu_label).join(" → ")}{seq.length > 14 ? ` → … (+${seq.length - 14})` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
